import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { Money } from '@/types/domain'

/** A row of four balance tiles (S-11 revision): «Счета клиенту»' and
 *  «Затраты»' totals. Two by two on a phone, four abreast from `lg`. gap-px
 *  over a hairline ground rules the cells in both axes without per-index
 *  border arithmetic (the dashboard tiles' idiom); bordered like a card, never
 *  shadowed. A labelled group, so a screen reader hears what the four are. */
export function BalanceTiles({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        'bg-line-hair border-border-subtle grid grid-cols-2 gap-px overflow-hidden',
        'rounded-[var(--radius-DEFAULT)] border lg:grid-cols-4',
      )}
    >
      {children}
    </div>
  )
}

/** One balance: eyebrow, figure, and the line saying where it came from. */
export function BalanceCell({
  label,
  amount,
  note,
  tone,
}: {
  label: string
  amount?: Money
  /** A line under the figure — or several, as block spans («Получено»'s
   *  «не разнесено» and «Возврат», S-22f). */
  note?: React.ReactNode
  tone?: 'attention'
}) {
  return (
    <div className="bg-surface flex min-w-0 flex-col gap-1.5 px-[18px] py-4 max-sm:px-3.5">
      <span className="asa-eyebrow">{label}</span>
      <span
        className={cn(
          /* Wraps rather than widens: on a phone two tiles share 390px, and
             the currency code drops under a long figure instead of pushing
             the page sideways. */
          'asa-num flex flex-wrap items-baseline gap-x-1 text-[17px] leading-none font-semibold',
          tone === 'attention' ? 'text-attention' : 'text-text-heading',
        )}
      >
        {amount ? (
          <>
            {formatNumber(amount.amount, 2)}
            <span className="text-text-muted text-[12px] font-normal">{amount.currency}</span>
          </>
        ) : (
          '—'
        )}
      </span>
      {note ? (
        <span className="text-text-muted text-[12px] leading-snug font-light">{note}</span>
      ) : null}
    </div>
  )
}
